<script>
import { mapState } from 'vuex'
import axios from '@/utils/AxiosService'
import { captureException } from '@/utils'
import LeaveReviewStarsInput from './LeaveReviewStarsInput.vue'
import LeaveReviewReviewersSelect from './LeaveReviewReviewersSelect.vue'

export default {
  components: {
    LeaveReviewStarsInput,
    LeaveReviewReviewersSelect,
  },

  props: {
    reviewer: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      rating: 0,
      reviewerUserId: null,
      description: '',
      saving: false,
    }
  },

  computed: {
    ...mapState('reports', ['inspection']),
  },

  beforeDestroy() {
    this.handleClose()
  },

  mounted() {
    this.reviewerUserId = this.reviewer.attributes?.user_id
    this.$modal.show('leave-review')
  },

  methods: {
    handleClose() {
      this.$modal.hide('leave-review')
      this.$emit('close')
    },

    handleClearAll() {
      this.rating = 0
      this.description = ''
    },

    async handleSave() {
      try {
        this.saving = true

        const data = {
          attributes: {
            id_token: this.$route.query.id_token,
            inspection_slug: this.inspection.attributes.slug,
            reviewed_from: 'Report Popup',
            on_behalf_of_user: this.reviewerUserId,
            rating: this.rating,
            description: this.description,
          },
        }

        await axios.post('/api/v2/public/inspection_reviews', { data })

        this.$store.dispatch('notifier/show', {
          type: 'Saved',
          msg: 'Thanks for your feedback!!',
        })

        this.handleClose()
      } catch (error) {
        captureException(error)
        this.$store.dispatch('notifier/show', {
          type: 'Error',
          msg: 'There was an error leaving your review!',
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<template>
  <modal
    id="leave-review"
    class="leave-review"
    name="leave-review"
    scrollable
    width="100%"
    height="auto"
    :click-to-close="false"
    @closed="handleClose"
  >
    <div
      class="
        tw-items-center tw-justify-center tw-flex
        lg:tw-max-w-[800px]
        tw-mx-auto tw-h-full
      "
    >
      <div class="tw-rounded-spf-md tw-bg-spf-white tw-w-full">
        <div class="tw-h-[8px] tw-rounded-t-spf-md tw-bg-primary" />
        <div class="tw-p-8 tw-py-0">
          <div class="tw-w-full tw-flex tw-justify-end tw-h-14 tw-items-center">
            <span
              class="tw-cursor-pointer fa-solid fa-xmark"
              @click="handleClose"
            />
          </div>
        </div>

        <div class="tw-p-20 tw-pt-0 tw-flex tw-flex-col tw-gap-9">
          <div class="tw-flex tw-flex-col tw-gap-2">
            <p class="tw-text-spf-2xl tw-font-spf-bold tw-text-spf-default">
              How did we do?
            </p>
            <p class="">Your feedback is very important to us!</p>
          </div>
          <leave-review-stars-input v-model="rating" />
          <div class="tw-flex tw-flex-col tw-gap-2">
            <label class="tw-text-spf-sm tw-text-spf-muted tw-font-spf-bold"
              >Leave feedback</label
            >
            <textarea
              v-model="description"
              :disabled="!rating"
              class="
                tw-rounded-spf
                tw-border
                tw-border-solid
                tw-shadow-spf-sm
                tw-bg-spf-default
                tw-border-spf-input
                disabled:tw-bg-spf-disabled
                focus:tw-border-transparent
                placeholder:tw-text-spf-subtle
                tw-h-32
                focus:tw-ring-2 focus:tw-ring-spf-primary
                hover:tw-border-spf-input-hover
                disabled:tw-border-spf-subtle
                disabled:tw-text-spf-disabled
                disabled:tw-placeholder:text-spf-disabled
                tw-py-2
                tw-px-3
                tw-text-base
                tw-font-normal
                tw-leading-relaxed
                tw-tracking-normal
              "
            />
          </div>

          <div class="tw-flex tw-flex-col tw-gap-2">
            <label class="tw-text-spf-sm tw-text-spf-muted tw-font-spf-bold"
              >Review as</label
            >
            <leave-review-reviewers-select
              v-model="reviewerUserId"
              :inspection="inspection"
            />
          </div>
        </div>
        <div
          class="
            tw-flex
            tw-h-16
            tw-shrink-0
            tw-items-center
            tw-justify-between
            tw-border-t
            tw-border-spf-subtle
            tw-px-6
            tw-py-5
            tw-gap-2
          "
        >
          <div
            class="
              tw-flex
              tw-justify-start
              tw-style-title-sm
              tw-text-center
              tw-whitespace-nowrap
              tw-flex-1
            "
          >
            <button
              class="
                tw-inline-flex tw-items-center tw-font-medium
                disabled:tw-pointer-events-none
                tw-rounded
                tw-style-button-lg
                tw-py-3
                tw-px-4
                tw-text-spf-primary
                tw-bg-spf-transparent
                hover:tw-bg-spf-primary-subtle
                focus:tw-outline-spf-primary
                disabled:tw-text-spf-disabled
                tw-w-full
                sm:tw-w-fit
              "
              data-testid="secondaryAction"
              @click="handleClearAll"
            >
              <span class="tw-w-full">Clear All</span>
            </button>
          </div>
          <div class="tw-flex tw-items-center tw-justify-end tw-flex-1">
            <button
              class="
                tw-inline-flex tw-items-center tw-font-medium
                tw-focus:outline-0
                disabled:tw-pointer-events-none
                tw-rounded
                tw-style-button-lg
                tw-py-3
                tw-px-4
                tw-text-spf-over-accent
                tw-bg-spf-primary
                hover:tw-bg-spf-primary-hover
                focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-spf-primary
                disabled:tw-bg-spf-disabled disabled:tw-text-spf-disabled
                tw-w-full
                sm:tw-w-fit
              "
              data-testid="leave-review-leave-feedback-btn"
              :disabled="!rating || saving"
              @click="handleSave"
            >
              <span class="tw-w-full">Leave Feedback</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<style lang="scss">
.leave-review {
  .v--modal-box.v--modal {
    background: transparent;
    box-shadow: none;
  }

  button:focus {
    color: initial;
    background-color: initial;
  }
}
</style>
